import { createContext, useContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { API } from '../services';
import { ErrorMessageContext } from './ErrorMessageContext';
import { ORGANISATION_KEYS, RESTRICT_DATA_DATE } from '../utilities/constants';
import { UserRolesAndPermissionsContext } from './UserRolesAndPermissionsContext';
import { getErrorMessage } from '../utilities/commonFunctions';

const HistoryAndReportsContext = createContext(null);

export const HistoryAndReportsProvider = ({ children }) => {
  const { userDetails } = useContext(UserRolesAndPermissionsContext);
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [vehicleOptionList, setVehicleOptionList] = useState([]);
  const [tripOptionList, setTripOptionList] = useState([]);
  const isFilterOptionsInitilised = useRef(false);
  const fetchVehicles = async () => {
    let page = 1;
    const uniqueVehicleNumbers = new Set();

    while (page !== null) {
      try {
        const { data } = await API.getUniqueVehicleDetails(page, 500);

        if (data?.results?.length) {
          data.results.forEach(item => {
            if (item?.cab_registration) {
              uniqueVehicleNumbers.add(item.cab_registration);
            }
          });

          const uniqueData = Array.from(uniqueVehicleNumbers).sort(() => 0);
          setVehicleOptionList(
            uniqueData.map(vehicle => ({
              label: vehicle,
              value: vehicle,
            }))
          );

          page = data?.next_page_number;
        } else {
          page = null;
        }
      } catch (error) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch vehicle list! Please try again after sometime.'
          )
        );
        page = null;
      }
    }
  };

  const fetchTrips = async (
    start_date = userDetails?.org === ORGANISATION_KEYS.JPMC
      ? RESTRICT_DATA_DATE?.JPMC
      : '',
    end_date = ''
  ) => {
    try {
      const { data } = await API.getTripsList({
        trip_status: '',
        start_date,
        end_date,
      });
      setTripOptionList(
        data?.trip_ids?.map(trip => ({
          label: trip,
          value: trip,
        })) || []
      );
    } catch (error) {
      if (error?.response?.status !== 404) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch trips list! Please try again after sometime.'
          )
        );
      }
      setTripOptionList([]);
    }
  };

  const fetchFilterOptionsList = (start_date, end_date) => {
    fetchTrips(start_date, end_date);

    if (!isFilterOptionsInitilised.current) {
      fetchVehicles();
      isFilterOptionsInitilised.current = true;
    }
  };

  const contextValue = useMemo(
    () => ({
      fetchFilterOptionsList,
      fetchTrips,
      vehicleOptionList,
      tripOptionList,
    }),
    [vehicleOptionList, tripOptionList, userDetails]
  );

  return (
    <HistoryAndReportsContext.Provider value={contextValue}>
      {children}
    </HistoryAndReportsContext.Provider>
  );
};

HistoryAndReportsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useHistoryAndReports = () => useContext(HistoryAndReportsContext);
